import React, { useEffect, useState } from "react";
import { useAuthStore } from "../../../context/useAuthStore";
import toast from "react-hot-toast";
import axios from "axios";
import StudentSubjectModal from "../../../components/ui/StudentSubjectModal";
import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber";
import { FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const Student = import.meta.env.VITE_STUDENT_ID;

type SubscriptionInfo = {
  subjectName: string;
  subscriptionId: string;
};

type Students = {
  fullName: string;
  phoneNumber: string;
  emailId: string;
  college: string;
  roleId: string;
  userId: string;
  registredSubscriptionsList: SubscriptionInfo[];
  verifiedSubscriptionsList: SubscriptionInfo[];
};

const phoneUtil = PhoneNumberUtil.getInstance();

const StudentsPage: React.FC = () => {
  const { user, token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState<Students[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<{
    fullName: string;
    phoneNumber: string;
    subjects: SubscriptionInfo[];
    viewType: "registered" | "subscribed";
  } | null>(null);

  const openModal = (
    fullName: string,
    phoneNumber: string,
    subjects: SubscriptionInfo[],
    viewType: "registered" | "subscribed"
  ) => {
    setModalData({ fullName, phoneNumber, subjects, viewType });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalData(null);
    fetchStudents();
  };

  const formatPhoneNumber = (number: string, region = "IN") => {
    try {
      const phoneNumber = phoneUtil.parseAndKeepRawInput(number, region);
      const isValidNumber = phoneUtil.isValidNumber(phoneNumber);
      let formatted = phoneUtil.format(phoneNumber, PhoneNumberFormat.NATIONAL);
      if (formatted.startsWith("0")) {
        formatted = formatted.substring(1);
      }
      return { formatted, isValidNumber };
    } catch {
      return { formatted: number, isValidNumber: false };
    }
  };

  const fetchStudents = async () => {
    if (!user?.userId) {
      toast.error("User not found.");
      return;
    }

    setLoading(true);

    try {
      const userRes = await axios.get(`${API_BASE_URL}/user?userId=`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      const allStudents: Students[] = userRes.data?.success || [];
      const filteredStudents = allStudents.filter((u) => u.roleId === Student);
      const studentWithSubjects = await Promise.all(
        filteredStudents.map(async (student) => {
          try {
            const subRes = await axios.get(
              `${API_BASE_URL}/subscription?userId=${student.userId}`,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                withCredentials: true,
              }
            );

            const subscriptions = subRes.data?.success || [];

            const registeredSubjectsList = subscriptions
              .filter((s: any) => s.status === "Registered")
              .map((s: any) => ({
                subjectName: s.subjectName,
                subscriptionId: s.subscriptionId,
              }));

            const verifiedSubjectsList = subscriptions
              .filter((s: any) => s.status === "Subscribed")
              .map((s: any) => ({
                subjectName: s.subjectName,
                subscriptionId: s.subscriptionId,
              }));

            return {
              ...student,
              registredSubscriptionsList: registeredSubjectsList,
              verifiedSubscriptionsList: verifiedSubjectsList,
            };
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (error) {
            return {
              ...student,
              registredSubscriptionsList: [],
              verifiedSubscriptionsList: [],
            };
          }
        })
      );

      setStudents(studentWithSubjects);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      toast.error("Failed to fetch students.");
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, [user?.userId, token]);

  const filteredStudents = students.filter(
    (student) =>
      student.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.phoneNumber.includes(searchQuery)
  );

  const exportToExcel = () => {
    if (students.length === 0) {
      toast.error("No student data to export");
      return;
    }

    // Prepare data for Excel
    const data = students.map((student, index) => ({
      "Sl.No": index + 1,
      "Full Name": student.fullName,
      "Phone Number": formatPhoneNumber(student.phoneNumber).formatted,
      "Email ID": formatPhoneNumber(student.emailId).formatted,
      College: formatPhoneNumber(student.college).formatted,
      "Registered Subjects": student.registredSubscriptionsList
        .map((s) => s.subjectName)
        .join(", "),
      "Verified Subjects": student.verifiedSubscriptionsList
        .map((s) => s.subjectName)
        .join(", "),
    }));

    // Create worksheet and workbook
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");

    // Generate Excel file buffer
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    // Create a blob and trigger download
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileName = `Students_${new Date().toISOString().split("T")[0]}.xlsx`;
    saveAs(blob, fileName);
  };

  return (
    <div className="min-h-[calc(100%-theme(space.2))] bg-myLight pb-4">
      <section className="border-b border-myInputBorder/60 bg-transparent backdrop-blur-md w-full sticky z-30 top-0 p-4">
        <div className="max-w-5xl mx-auto px-4">
          <h1 className="md:text-2xl sm:text-xl lext-lg font-bold text-myLabelText capitalize">
            Students
          </h1>
        </div>
      </section>

      <section className="max-w-5xl mx-auto pt-4 px-4">
        <div className="mb-4 flex gap-2">
          <input
            type="text"
            placeholder="Search by name or phone number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-gray-200"
          />
          <button
            className="customQuestionBtn uppercase"
            onClick={exportToExcel}
          >
            <FaFileExcel size={16} />{" "}
            <span className="hidden sm:block">export</span>
          </button>
        </div>

        {loading ? (
          <p className="subjectNotFound">Loading...</p>
        ) : filteredStudents.length === 0 ? (
          <p className="subjectNotFound">No students found.</p>
        ) : (
          <section className="subjectTableSection">
            <div className="overflow-x-auto">
              <div className="max-h-[calc(100vh-theme(space.56))] overflow-y-auto">
                <table className="min-w-full border-collapse">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="bg-gray-200">
                      <th className="tableHeadValue py-1" rowSpan={2}>
                        Sl.No
                      </th>
                      <th className="tableHeadValue py-1" rowSpan={2}>
                        Student Name
                      </th>
                      <th className="tableHeadValue py-1" rowSpan={2}>
                        Phone Number
                      </th>
                      <th
                        className="tableHeadValue py-1 text-center"
                        colSpan={2}
                      >
                        Subject
                      </th>
                    </tr>
                    <tr className="bg-gray-200">
                      <th className="tableHeadValue py-1 text-center">
                        Registered
                      </th>
                      <th className="tableHeadValue py-1 text-center">
                        Verified
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((student, index) => {
                      const { formatted, isValidNumber } = formatPhoneNumber(
                        student.phoneNumber
                      );
                      return (
                        <tr key={index} className="tableBodyRow">
                          <td className="tableBodyValue">{index + 1}</td>
                          <td className="tableBodyValue">{student.fullName}</td>
                          <td
                            className={`tableBodyValue ${
                              !isValidNumber ? "underline text-red-600" : ""
                            }`}
                          >
                            {formatted}
                          </td>
                          <td
                            className={`tableBodyValue text-center ${
                              student.registredSubscriptionsList?.length === 0
                                ? "text-gray-400 cursor-default pointer-events-none"
                                : "cursor-pointer underline text-gray-600"
                            }`}
                          >
                            <span
                              className="inline-flex items-center rounded-md bg-info-50 px-2 py-1 text-sm font-medium text-info-600 inset-ring inset-ring-info-500/10"
                              onClick={() =>
                                openModal(
                                  student.fullName,
                                  student.phoneNumber,
                                  student.registredSubscriptionsList,
                                  "registered"
                                )
                              }
                            >
                              {student.registredSubscriptionsList?.length || 0}
                            </span>
                          </td>
                          <td
                            className={`tableBodyValue text-center ${
                              student.verifiedSubscriptionsList?.length === 0
                                ? "text-gray-400 cursor-default pointer-events-none"
                                : "cursor-pointer underline text-gray-600"
                            }`}
                          >
                            <span
                              className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-sm font-medium text-green-600 inset-ring inset-ring-green-500/10"
                              onClick={() =>
                                openModal(
                                  student.fullName,
                                  student.phoneNumber,
                                  student.verifiedSubscriptionsList,
                                  "subscribed"
                                )
                              }
                            >
                              {student.verifiedSubscriptionsList?.length || 0}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}
      </section>

      {isModalOpen && modalData && (
        <StudentSubjectModal
          fullName={modalData.fullName}
          phoneNumber={formatPhoneNumber(modalData.phoneNumber).formatted}
          subjects={modalData.subjects}
          viewType={modalData.viewType}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default StudentsPage;
